import React, { useState, useEffect, useRef } from "react";
import {
  faPencil,
  faLink,
  faRotate,
  faTrash,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast";
import LoadingSpinner from "../../components/LoadingSpinner";
import { PermissionWrapper, hasPermission } from "../../helpers/permission.js";

const WebsiteSitemapList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [totalEntries, setTotalEntries] = useState(0);
  const [requestingId, setRequestingId] = useState(0);
  const [deletingId, setDeletingId] = useState(0);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);
  const toastShownRef = useRef(false);

  // 🏷️ Review Type Toggle
  const [reviewType, setReviewType] = useState("streetgains"); // "streetgains" (0) or "streetfolio" (1)

  const navigate = useNavigate();

  const fetchSitemapData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const productApp = reviewType === "streetfolio" ? 1 : 0;

      const response = await axios.get(
        `${API_URL}siteMap/sitemap?product_app=${productApp}&limit=${entriesPerPage}&page=${currentPage}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data && response.data.success) {
        if (!toastShownRef.current) {
          showToast.success("Sitemap data fetched successfully");
          toastShownRef.current = true;
        }
        const apiData = response.data.data.sitemap;
        setPages(Array.isArray(apiData) ? apiData : []);
        setTotalEntries(response.data.data.totalRecords || 0);
      } else {
        setPages([]);
        setTotalEntries(0);
        showToast.error("No data found");
      }
    } catch (error) {
      setPages([]);
      setTotalEntries(0);
      const errorMessage =
        error.response?.data?.message || "Failed to fetch sitemap data";
      showToast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle open website functionality
  const handleOpenWebsite = (page) => {
    if (page.pageUrl) {
      window.open(page.pageUrl, "_blank", "noopener,noreferrer");
    } else {
      showToast.warning("No URL available for this page");
    }
  };

  const handleRequestIndex = async (pageId) => {
    try {
      setRequestingId(pageId);
      const token = localStorage.getItem("authToken");
      await axios.patch(
        `${API_URL}siteMap/request-index/${pageId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      showToast.success("Index request sent successfully");
      fetchSitemapData();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to request index. Please try again.";
      showToast.error(errorMessage);
    } finally {
      setRequestingId(0);
    }
  };

  const handleDelete = (pageId, pageName) => {
    setSelectedDeleteItem({ id: pageId, name: pageName });
    setDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    if (!selectedDeleteItem) return;

    try {
      setDeletingId(selectedDeleteItem.id);
      const token = localStorage.getItem("authToken");
      await axios.delete(`${API_URL}siteMap/delete/${selectedDeleteItem.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      showToast.success("Sitemap page deleted successfully");
      fetchSitemapData();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to delete sitemap page. Please try again.";
      showToast.error(errorMessage);
    } finally {
      setDeletingId(0);
      setDeleteModalVisible(false);
      setSelectedDeleteItem(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Toggle between review types
  const toggleReviewType = (type) => {
    setReviewType(type);
    setSearchTerm("");
    setCurrentPage(1);
  };

  // Fetch data when component mounts or when review type/entries per page/current page changes
  useEffect(() => {
    fetchSitemapData();
  }, [reviewType, entriesPerPage, currentPage]);

  useEffect(() => {
    if (!pages.length) return;

    setIsFiltering(true);

    const timer = setTimeout(() => {
      setIsFiltering(false);
    }, 300); // smooth UX

    return () => clearTimeout(timer);
  }, [searchTerm, reviewType, entriesPerPage, currentPage]);


  const filteredPages = Array.isArray(pages)
    ? pages.filter(
      (page) =>
        page &&
        page.pageName &&
        page.pageName.toLowerCase().includes(searchTerm.toLowerCase())
    )
    : [];

  const indexOfLastItem = currentPage * entriesPerPage;
  const indexOfFirstItem = indexOfLastItem - entriesPerPage;
  const currentItems = searchTerm
    ? filteredPages.slice(indexOfFirstItem, indexOfLastItem)
    : pages;
  const totalPages = searchTerm
    ? Math.ceil(filteredPages.length / entriesPerPage)
    : Math.ceil(totalEntries / entriesPerPage);
  const displayTotalEntries = searchTerm ? filteredPages.length : totalEntries;

  const handleEntriesPerPageChange = (value) => {
    setEntriesPerPage(Number(value));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Calculate start and end for pagination display
  const start = (currentPage - 1) * entriesPerPage + 1;
  const end = Math.min(currentPage * entriesPerPage, displayTotalEntries);

  // if (loading && pages.length === 0) {
  //   return <LoadingSpinner />;
  // }

  useEffect(() => {
    const handleClearFilters = () => {
      setSearchTerm("");
    };
    window.addEventListener('clearWebsiteSitemapFilterEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearWebsiteSitemapFilterEvent', handleClearFilters);
    };
  }, []);

  return (
    <div className="page-container mt-5">
      {/* {loading && <LoadingSpinner />} */}

      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center">
          <Link to="/" className="text-primary text-decoration-none">
            <i className="fas fa-home me-1"></i> Home
          </Link>
          <span className="mx-2">/</span>
          <span>Manage Sitemap</span>
        </div>

        <div className="d-flex align-items-center">
          {hasPermission(reviewType === "streetgains" ? "streetgains_sitemap" : "streetfolio_sitemap", "add") && (
            <button
              className="btn btn-default btn-sm"
              onClick={() => navigate(`/Website/Sitemap/add`)}
            >
              + Add Page
            </button>
          )}

          {/* 📑 Pagination Controls - Added near Add Page button */}
          {pages.length > 0 && (
            <div className="d-inline-block me-3 ms-3">
              <span style={{ fontSize: "0.8rem" }} className="me-2">
                {start} to {end} of {displayTotalEntries}
              </span>
              <button
                className="btn btn-default btn-sm"
                title="Previous"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              &nbsp;
              <button
                className="btn btn-default btn-sm"
                title="Next"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Review Type Toggle */}
      <div className="panel panel-default with-rounded-corner with-shadow mb-3">
        <div className="panel-body">
          <div className="row">
            <div className="col-md-12">
              <label className="me-3">Select Sitemap Type:</label>
              <div className="btn-group" role="group">
                {/* {hasPermission("streetgains_sitemap", "view") && ( */}
                <button
                  type="button"
                  className={`btn ${reviewType === "streetgains" ? "btn-primary" : "btn-default"}`}
                  onClick={() => toggleReviewType("streetgains")}
                >
                  Streetgains Sitemap
                </button>
                {/* )} */}
                {/* {hasPermission("streetfolio_sitemap", "view") && ( */}
                <button
                  type="button"
                  className={`btn ${reviewType === "streetfolio" ? "btn-primary" : "btn-default"}`}
                  onClick={() => toggleReviewType("streetfolio")}
                >
                  Streetfolio Sitemap
                </button>
                {/* )} */}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="panel panel-default with-rounded-corner with-shadow p-10">
        <div className="panel-body">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center">
              <span style={{ marginRight: "8px" }}>Show</span>
              <select
                className="form-select form-select-sm mx-2"
                style={{ width: "70px" }}
                value={entriesPerPage}
                onChange={(e) => handleEntriesPerPageChange(e.target.value)}
                disabled={loading}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
              <span>entries</span>
            </div>
            <div className="d-flex align-items-center">
              <label htmlFor="search" style={{ marginRight: "8px" }}>
                Search:
              </label>
              <input
                id="search"
                type="text"
                className="form-control form-control-sm"
                style={{ width: "200px" }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          <div className="table-responsive user-table-container">
            <style>
              {`
                  .user-table-container {
                    position: relative;
                    max-height: 71vh;
                    overflow-y: auto;
                    overflow-x: auto;
                   }

                  /* First header row (titles) */
                  .user-table-container table thead tr:nth-child(1) th {
                    position: sticky;
                    top: -1px;
                    z-index: 30;
                    background: #f8f9fa;
                  }

                 /* Second header row (search inputs) */
                 .user-table-container table thead tr:nth-child(2) th {
                   position: sticky;
                   top: 25px; /* adjust based on header height */
                   z-index: 25;
                   background: #fff;
                  }
               `}
            </style>
            <table className="table table-condensed table-bordered bg-white">
              <thead>
                <tr>
                  <th style={{ width: "50px", textAlign: "center" }}>#</th>
                  <th>Page Name</th>
                  <th style={{ width: "150px" }}>Indexed On</th>
                  <th style={{ width: "120px" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {(loading || isFiltering) ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <tr key={i}>
                      <td><div className="skeleton-box" /></td>
                      <td><div className="skeleton-box" /></td>
                      <td><div className="skeleton-box" /></td>
                      <td><div className="skeleton-box" /></td>
                    </tr>
                  ))
                ) : currentItems.length > 0 ? (
                  currentItems.map((page, index) => (
                    <tr key={page.id || index}>
                      <td style={{ textAlign: "center" }}>
                        {indexOfFirstItem + index + 1}
                      </td>
                      <td>{page.pageName || "N/A"}</td>
                      <td>{formatDate(page.indexedOn)}</td>
                      <td>
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-link btn-xs me-2"
                            title="Open Link"
                            onClick={() => handleOpenWebsite(page)}
                          >
                            <FontAwesomeIcon icon={faLink} />
                          </button>

                          <button
                            className="btn btn-primary btn-xs me-2"
                            title="Request Index"
                            onClick={() => handleRequestIndex(page.id)}
                            disabled={requestingId === page.id}
                          >
                            {requestingId === page.id ? (
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                              ></span>
                            ) : (
                              <FontAwesomeIcon icon={faLink} />
                            )}
                          </button>
                          {hasPermission(reviewType === "streetgains" ? "streetgains_sitemap" : "streetfolio_sitemap", "edit") && (
                            <button
                              className="btn btn-warning btn-xs me-2"
                              title="Edit"
                              onClick={() => navigate(`/Website/Sitemap/edit/${page.id}`)}
                            >
                              <FontAwesomeIcon icon={faPencil} />
                            </button>
                          )}

                          {hasPermission(reviewType === "streetgains" ? "streetgains_sitemap" : "streetfolio_sitemap", "delete") && (
                            <button
                              className="btn btn-danger btn-xs me-2"
                              title="Delete"
                              onClick={() => handleDelete(page.id, page.pageName)}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center">
                      {loading ? "Loading..." : `No ${reviewType === "streetfolio" ? "Streetfolio" : "Streetgains"} sitemap data available`}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      {deleteModalVisible && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Deletion</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setDeleteModalVisible(false)}
                ></button>
              </div>
              <div className="modal-body">
                Are you sure you want to delete{" "}
                <strong>{selectedDeleteItem?.name}</strong>?
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setDeleteModalVisible(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDelete}
                  disabled={deletingId === selectedDeleteItem?.id}
                >
                  {deletingId === selectedDeleteItem?.id ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                    ></span>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebsiteSitemapList;